import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { Sun, Moon } from 'lucide-react'
import { useTheme } from '../hooks/useTheme'
import { useApiLogs } from '../contexts/ApiLogsContext'
import msLogoLight from '../assets/images/ms_logo_light.png'
import msLogoDark from '../assets/images/ms_logo_dark.png'

const USPSetupPage: React.FC = () => {
  const navigate = useNavigate()
  const isDark = useTheme()
  const { clearApiLogs, clearSelectionHistory } = useApiLogs()
  const [integrationMethod, setIntegrationMethod] = useState<'api' | 'sdk'>('api')
  const [apiKey, setApiKey] = useState('')
  const [pubUserId, setPubUserId] = useState('')
  const [sdkId, setSdkId] = useState('')
  const [sdkPubUserId, setSdkPubUserId] = useState('')
  const [errors, setErrors] = useState<{apiKey?: string, pubUserId?: string, sdkId?: string, sdkPubUserId?: string}>({})
  const [usingDemoCredentials, setUsingDemoCredentials] = useState(false)

  const validateForm = () => {
    const newErrors: {apiKey?: string, pubUserId?: string, sdkId?: string, sdkPubUserId?: string} = {}
    
    if (integrationMethod === 'api') {
      if (!apiKey.trim()) {
        newErrors.apiKey = 'API Key is required'
      } else if (apiKey.length < 10) {
        newErrors.apiKey = 'API Key must be at least 10 characters'
      }
      
      if (!pubUserId.trim()) {
        newErrors.pubUserId = 'Publisher User ID is required'
      } else if (pubUserId.length < 3) {
        newErrors.pubUserId = 'Publisher User ID must be at least 3 characters'
      }
    } else if (integrationMethod === 'sdk') {
      if (!sdkId.trim()) {
        newErrors.sdkId = 'SDK ID is required'
      } else if (sdkId.length < 10) {
        newErrors.sdkId = 'SDK ID must be at least 10 characters'
      }
      
      if (!sdkPubUserId.trim()) {
        newErrors.sdkPubUserId = 'Publisher User ID is required'
      } else if (sdkPubUserId.length < 3) {
        newErrors.sdkPubUserId = 'Publisher User ID must be at least 3 characters'
      }
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleContinue = () => {
    if (validateForm()) {
      // Clear all previous session data for a fresh start
      clearApiLogs()
      clearSelectionHistory()
      
      // Clear USP session storage data to ensure completely fresh start
      sessionStorage.removeItem('usp_session_id')
      
      if (integrationMethod === 'api') {
        // Store the values in sessionStorage for the API demo
        sessionStorage.setItem('usp_api_key', apiKey)
        sessionStorage.setItem('usp_pub_user_id', pubUserId)
        sessionStorage.setItem('usp_integration_method', 'api')
        // Set navigation source flag to indicate coming from setup page
        sessionStorage.setItem('usp_navigation_source', 'setup_page')
        
        console.log('🔄 Starting new API demo session - cleared all previous data')
        navigate('/usp-demo')
      } else if (integrationMethod === 'sdk') {
        // Store the SDK ID and publisher user ID for the SDK demo
        sessionStorage.setItem('usp_sdk_id', sdkId)
        sessionStorage.setItem('usp_pub_user_id', sdkPubUserId)
        sessionStorage.setItem('usp_integration_method', 'sdk')
        sessionStorage.setItem('usp_navigation_source', 'setup_page')
        
        console.log('🔄 Starting new SDK demo session')
        navigate('/usp-sdk-demo')
      }
    }
  }

  const handleSampleData = () => {
    if (integrationMethod === 'api') {
      setApiKey('b9a31935-a222-4bdd-bfc6-a63e6bb01e39')
      setPubUserId('MomentScience_USP_Demo')
    } else if (integrationMethod === 'sdk') {
      setSdkId('96c6df58ad0e017b')
      setSdkPubUserId('MomentScience_USP_Demo')
    }
    setUsingDemoCredentials(true)
    setErrors({})
  }

  const handleClearCredentials = () => {
    setApiKey('')
    setPubUserId('')
    setSdkId('')
    setSdkPubUserId('')
    setUsingDemoCredentials(false)
    setErrors({})
  }

  const toggleTheme = () => {
    const newTheme = isDark ? 'light' : 'dark'
    localStorage.setItem('theme', newTheme)
    if (newTheme === 'dark') {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <div className="min-h-screen flex">
        {/* Left Column - Branding & Description */}
        <div className="flex-1 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 flex items-center justify-center p-12">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="max-w-md"
          >
            {/* Logo */}
            <div className="flex items-center mb-8">
              <img 
                src={isDark ? msLogoDark : msLogoLight} 
                alt="MomentScience" 
                className="h-12 w-auto"
              />
            </div>
            
            {/* Title */}
            <h1 className="text-4xl font-bold text-gray-800 dark:text-white mb-6">
              User Selected Perks Demo
            </h1>
            
            {/* Description */}
            <p className="text-gray-600 dark:text-gray-300 text-lg leading-relaxed mb-8">
              Experience personalized offer selection during the pre-checkout phase. 
              See how customers can choose their preferred rewards and perks.
            </p>

            {/* Features */}
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <span className="text-gray-700 dark:text-gray-300">Personalized perk selection</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <span className="text-gray-700 dark:text-gray-300">Real-time offer integration</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <span className="text-gray-700 dark:text-gray-300">Enhanced customer engagement</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Right Column - Form */}
        <div className="flex-1 flex items-center justify-center p-12">
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="w-full max-w-md"
          >
            <h2 className="text-3xl font-bold text-gray-800 dark:text-white mb-8">
              Get Started
            </h2>

            <div className="space-y-6">
              {/* Integration Method Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  INTEGRATION METHOD
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setIntegrationMethod('api')
                      setErrors({})
                      setUsingDemoCredentials(false)
                      setApiKey('')
                      setPubUserId('')
                      setSdkId('')
                      setSdkPubUserId('')
                    }}
                    className={`p-4 border-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      integrationMethod === 'api'
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                        : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <div className="text-center">
                      <div className="text-2xl mb-2">🔗</div>
                      <div className="font-semibold mb-1">API Integration</div>
                      <div className="text-xs opacity-75">Direct API calls</div>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIntegrationMethod('sdk')
                      setErrors({})
                      setUsingDemoCredentials(false)
                      setApiKey('')
                      setPubUserId('')
                      setSdkId('')
                      setSdkPubUserId('')
                    }}
                    className={`p-4 border-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      integrationMethod === 'sdk'
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                        : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <div className="text-center">
                      <div className="text-2xl mb-2">⚡</div>
                      <div className="font-semibold mb-1">SDK Integration</div>
                      <div className="text-xs opacity-75">JavaScript SDK</div>
                    </div>
                  </button>
                </div>
              </div>

              {/* Conditional Form Fields */}
              {integrationMethod === 'api' && (
                <>
                  {/* API Key Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      API KEY
                    </label>
                <input
                  type={usingDemoCredentials ? "password" : "text"}
                  value={apiKey}
                  onChange={(e) => {
                    if (!usingDemoCredentials) {
                      setApiKey(e.target.value)
                      if (errors.apiKey) {
                        setErrors(prev => ({ ...prev, apiKey: undefined }))
                      }
                    }
                  }}
                  placeholder="Enter your API key"
                  readOnly={usingDemoCredentials}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                    errors.apiKey 
                      ? 'border-red-300 bg-red-50 dark:border-red-600 dark:bg-red-900/10' 
                      : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800'
                  } ${
                    usingDemoCredentials 
                      ? 'bg-gray-50 dark:bg-gray-700 cursor-not-allowed text-gray-500' 
                      : 'text-gray-900 dark:text-white'
                  }`}
                />
                {errors.apiKey && (
                  <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                    {errors.apiKey}
                  </p>
                )}
              </div>

              {/* Publisher User ID Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  PUBLISHER USER ID
                </label>
                <input
                  type="text"
                  value={pubUserId}
                  onChange={(e) => {
                    if (!usingDemoCredentials) {
                      setPubUserId(e.target.value)
                      if (errors.pubUserId) {
                        setErrors(prev => ({ ...prev, pubUserId: undefined }))
                      }
                    }
                  }}
                  placeholder="Enter your pub_user_id"
                  readOnly={usingDemoCredentials}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                    errors.pubUserId 
                      ? 'border-red-300 bg-red-50 dark:border-red-600 dark:bg-red-900/10' 
                      : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800'
                  } ${
                    usingDemoCredentials 
                      ? 'bg-gray-50 dark:bg-gray-700 cursor-not-allowed text-gray-500' 
                      : 'text-gray-900 dark:text-white'
                  }`}
                />
                {errors.pubUserId && (
                  <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                    {errors.pubUserId}
                  </p>
                )}
              </div>
                </>
              )}

              {integrationMethod === 'sdk' && (
                <>
                  {/* SDK ID Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      SDK ID
                    </label>
                    <input
                      type={usingDemoCredentials ? "password" : "text"}
                      value={sdkId}
                      onChange={(e) => {
                        if (!usingDemoCredentials) {
                          setSdkId(e.target.value)
                          if (errors.sdkId) {
                            setErrors(prev => ({ ...prev, sdkId: undefined }))
                          }
                        }
                      }}
                      placeholder="Enter your SDK ID"
                      readOnly={usingDemoCredentials}
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                        errors.sdkId 
                          ? 'border-red-300 bg-red-50 dark:border-red-600 dark:bg-red-900/10' 
                          : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800'
                      } ${
                        usingDemoCredentials 
                          ? 'bg-gray-50 dark:bg-gray-700 cursor-not-allowed text-gray-500' 
                          : 'text-gray-900 dark:text-white'
                      }`}
                    />
                    {errors.sdkId && (
                      <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                        {errors.sdkId}
                      </p>
                    )}
                  </div>

                  {/* SDK Publisher User ID Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      PUBLISHER USER ID
                    </label>
                    <input
                      type="text"
                      value={sdkPubUserId}
                      onChange={(e) => {
                        if (!usingDemoCredentials) {
                          setSdkPubUserId(e.target.value)
                          if (errors.sdkPubUserId) {
                            setErrors(prev => ({ ...prev, sdkPubUserId: undefined }))
                          }
                        }
                      }}
                      placeholder="Enter your pub_user_id"
                      readOnly={usingDemoCredentials}
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                        errors.sdkPubUserId 
                          ? 'border-red-300 bg-red-50 dark:border-red-600 dark:bg-red-900/10' 
                          : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800'
                      } ${
                        usingDemoCredentials 
                          ? 'bg-gray-50 dark:bg-gray-700 cursor-not-allowed text-gray-500' 
                          : 'text-gray-900 dark:text-white'
                      }`}
                    />
                    {errors.sdkPubUserId && (
                      <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                        {errors.sdkPubUserId}
                      </p>
                    )}
                  </div>
                </>
              )}

              {/* Demo Credentials Toggle */}
              <div className="flex items-center justify-between py-4">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Use demo credentials
                </span>
                <button
                  type="button"
                  onClick={() => {
                    if (usingDemoCredentials) {
                      handleClearCredentials()
                    } else {
                      handleSampleData()
                    }
                  }}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                    usingDemoCredentials
                      ? 'bg-blue-600'
                      : 'bg-gray-200 dark:bg-gray-700'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      usingDemoCredentials ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* Start Demo Button */}
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleContinue}
                className="w-full px-6 py-4 bg-[#FE4215] hover:bg-[#E53910] text-white rounded-lg font-medium transition-colors"
              >
                Start Demo →
              </motion.button>

              {/* Back Link */}
              <div className="text-center">
                <button
                  onClick={() => navigate('/')}
                  className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                >
                  ← Back to Home
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* API Integration Overview Section */}
      <div className="bg-gray-50 dark:bg-gray-800 py-16">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-800 dark:text-white mb-4">
                API Integration Overview
              </h2>
              <p className="text-gray-600 dark:text-gray-300 text-lg max-w-3xl mx-auto">
                This demo showcases real-time integration with MomentScience APIs for User Selected Perks functionality.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Offers API */}
              <div className="bg-white dark:bg-gray-700 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-600 hover:shadow-md transition-shadow">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900 dark:to-blue-800 rounded-xl flex items-center justify-center mr-4">
                    <span className="text-2xl">🎯</span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Offers API</h3>
                </div>
                <div className="mb-4">
                  <code className="text-sm bg-gray-100 dark:bg-gray-600 px-3 py-1 rounded text-gray-800 dark:text-gray-200">
                    POST /native/v2/offers.json
                  </code>
                </div>
                <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">
                  Fetches available offers based on user targeting and placement configuration
                </p>
                <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full mr-2"></div>
                    Real-time offer retrieval
                  </li>
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full mr-2"></div>
                    Country and demographic targeting
                  </li>
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full mr-2"></div>
                    Session ID generation
                  </li>
                </ul>
              </div>

              {/* Selection API */}
              <div className="bg-white dark:bg-gray-700 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-600 hover:shadow-md transition-shadow">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-emerald-100 to-emerald-200 dark:from-emerald-900 dark:to-emerald-800 rounded-xl flex items-center justify-center mr-4">
                    <span className="text-2xl">✨</span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Selection API</h3>
                </div>
                <div className="mb-4">
                  <code className="text-xs bg-gray-100 dark:bg-gray-600 px-3 py-1 rounded text-gray-800 dark:text-gray-200 break-all">
                    POST /sdk/v4/usp/{`{sessionId}`}/{`{campaignId}`}/select.json
                  </code>
                </div>
                <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">
                  Records when users select specific offers for follow-up delivery
                </p>
                <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full mr-2"></div>
                    Individual offer selection tracking
                  </li>
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full mr-2"></div>
                    Bearer token authentication
                  </li>
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full mr-2"></div>
                    Real-time selection logging
                  </li>
                </ul>
              </div>

              {/* Unselect API */}
              <div className="bg-white dark:bg-gray-700 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-600 hover:shadow-md transition-shadow">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-rose-100 to-rose-200 dark:from-rose-900 dark:to-rose-800 rounded-xl flex items-center justify-center mr-4">
                    <span className="text-2xl">🚫</span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Unselect API</h3>
                </div>
                <div className="mb-4">
                  <code className="text-xs bg-gray-100 dark:bg-gray-600 px-3 py-1 rounded text-gray-800 dark:text-gray-200 break-all">
                    POST /sdk/v4/usp/{`{sessionId}`}/{`{campaignId}`}/unselect.json
                  </code>
                </div>
                <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">
                  Records when users deselect previously selected offers
                </p>
                <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full mr-2"></div>
                    Selection state management
                  </li>
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full mr-2"></div>
                    User preference tracking
                  </li>
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full mr-2"></div>
                    Dynamic offer management
                  </li>
                </ul>
              </div>

              {/* Session Management */}
              <div className="bg-white dark:bg-gray-700 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-600 hover:shadow-md transition-shadow">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-100 to-purple-200 dark:from-purple-900 dark:to-purple-800 rounded-xl flex items-center justify-center mr-4">
                    <span className="text-2xl">⚙️</span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Session Management</h3>
                </div>
                <div className="mb-4">
                  <code className="text-xs bg-gray-100 dark:bg-gray-600 px-3 py-1 rounded text-gray-800 dark:text-gray-200 break-all">
                    PUT /sdk/v4/usp/session/{`{sessionId}`}.json
                  </code>
                </div>
                <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">
                  Updates session with bulk selection/unselection operations
                </p>
                <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full mr-2"></div>
                    Bulk selection updates
                  </li>
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full mr-2"></div>
                    Session state persistence
                  </li>
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full mr-2"></div>
                    Campaign grouping
                  </li>
                </ul>
              </div>

              {/* Session Details */}
              <div className="bg-white dark:bg-gray-700 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-600 hover:shadow-md transition-shadow">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-indigo-100 to-indigo-200 dark:from-indigo-900 dark:to-indigo-800 rounded-xl flex items-center justify-center mr-4">
                    <span className="text-2xl">📊</span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Session Details</h3>
                </div>
                <div className="mb-4">
                  <code className="text-xs bg-gray-100 dark:bg-gray-600 px-3 py-1 rounded text-gray-800 dark:text-gray-200 break-all">
                    GET /sdk/v4/usp/session/{`{sessionId}`}.json
                  </code>
                </div>
                <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">
                  Retrieves current session state and selected offers
                </p>
                <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full mr-2"></div>
                    Session state inspection
                  </li>
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full mr-2"></div>
                    Selected campaigns list
                  </li>
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full mr-2"></div>
                    Debugging and validation
                  </li>
                </ul>
              </div>

              {/* Wrap Session */}
              <div className="bg-white dark:bg-gray-700 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-600 hover:shadow-md transition-shadow">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-amber-100 to-amber-200 dark:from-amber-900 dark:to-amber-800 rounded-xl flex items-center justify-center mr-4">
                    <span className="text-2xl">🎁</span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Wrap Session</h3>
                </div>
                <div className="mb-4">
                  <code className="text-xs bg-gray-100 dark:bg-gray-600 px-3 py-1 rounded text-gray-800 dark:text-gray-200 break-all">
                    POST /sdk/v4/usp/session/{`{sessionId}`}.json
                  </code>
                </div>
                <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">
                  Finalizes the session and triggers offer delivery workflows
                </p>
                <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full mr-2"></div>
                    Session completion
                  </li>
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full mr-2"></div>
                    Webhook trigger initiation
                  </li>
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full mr-2"></div>
                    Follow-up workflow activation
                  </li>
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Floating Theme Toggle Button */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={toggleTheme}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-[#FE4215] hover:bg-[#E53910] text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center"
        title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
      >
        <motion.div
          initial={false}
          animate={{ rotate: isDark ? 180 : 0 }}
          transition={{ duration: 0.3 }}
        >
          {isDark ? (
            <Sun className="w-6 h-6" />
          ) : (
            <Moon className="w-6 h-6" />
          )}
        </motion.div>
      </motion.button>
    </div>
  )
}

export default USPSetupPage
